import {
  SOLANA_REQUESTS,
  SOLANA_RESPONSES,
  SOLANA_SIGNING_METHODS,
  solanaSignTransactionSchema,
} from "@/data/methods/Solana.methods";
import {
  acceptRequest,
  Errors,
  isCanceledError,
  rejectRequest,
} from "@/hooks/requestHandlers/utils";
import { Account, WalletAPIClient } from "@ledgerhq/wallet-api-client";
import { IWalletKit } from "@reown/walletkit";
import { VersionedTransaction } from "@solana/web3.js";
import base58 from "bs58";
import { findSignerAccount, toLiveTransaction } from "./utils";

export async function signTransaction(
  request: SOLANA_REQUESTS,
  topic: string,
  id: number,
  _chainId: string,
  accounts: Account[],
  client: WalletAPIClient,
  walletKit: IWalletKit,
) {
  if (request.method !== SOLANA_SIGNING_METHODS.SOLANA_SIGN_TRANSACTION) {
    throw new Error(
      `Method ${request.method} from request can not be used to sign transaction`,
    );
  }

  const params = solanaSignTransactionSchema.parse(request.params);

  const liveTransaction = toLiveTransaction(params.transaction);
  const account = findSignerAccount(params.transaction, accounts);

  if (params.pubkey && account.address !== params.pubkey) {
    throw new Error(
      `The provided pubkey ${params.pubkey} does not match the account address ${account.address} found in the transaction`,
    );
  }

  try {
    const signature = await client.transaction.sign(
      account.id,
      liveTransaction,
    );

    const transaction = VersionedTransaction.deserialize(signature);
    // The account is not always the fee payer at index 0: in a Jupiter gasless swap, the market
    // maker pays the fees and signs after it. Return the signature in the account's own slot.
    const signerIndex = transaction.message.staticAccountKeys.findIndex(
      (key) => key.toBase58() === account.address,
    );

    const result: SOLANA_RESPONSES[typeof request.method] = {
      signature: base58.encode(transaction.signatures[signerIndex]),
      transaction: signature.toString("base64"),
    };

    await acceptRequest(walletKit, topic, id, result);
  } catch (error) {
    if (isCanceledError(error)) {
      await rejectRequest(walletKit, topic, id, Errors.userDecline);
    } else {
      throw error;
    }
  }
}
